import {AfterViewInit, Component, computed, inject, Injector, ChangeDetectionStrategy, signal} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatCheckboxModule} from "@angular/material/checkbox"
import {MatInputModule} from "@angular/material/input"
import {MatMenu, MatMenuItem, MatMenuModule} from "@angular/material/menu"
import {MatSelectModule} from "@angular/material/select"
import {JobTaskDetailsFragment} from "@app/platform/components/jobs/job-task-details/job-task-details.generated"
import {formatRenderJobTaskClaimedByField, isClaimedRenderJobTask} from "@app/platform/models/job-task/render-job-task"
import {IsDefined} from "@cm/utils"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {OrganizationSelectComponent} from "@common/components/inputs/select/organization-select/organization-select.component"
import {SectionComponent} from "@common/components/item/item-details/section/section.component"
import {IsLoadingDirective} from "@common/directives"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DialogSize} from "@common/models/dialogs"
import {CascadeStepStatus, ContentTypeModel, MutationUpdateJobTaskInput, ResourceGroupId} from "@generated"
import {BaseDetailsComponent} from "@platform/components/base/base-details/base-details.component"
import {FilesSectionComponent} from "@platform/components/details/files-section/files-section.component"
import {TitleSectionComponent} from "@platform/components/details/title-section/title-section.component"
import {
    JobTaskDetailsGQL,
    UpdateJobTaskDetailsGQL,
    UpdateJobTaskResourceGroupDetailsGQL,
} from "@platform/components/jobs/job-task-details/job-task-details.generated"
import {DetailsDialogLayoutComponent} from "@platform/components/layouts/details-dialog-layout/details-dialog-layout.component"
import {CopyValueToClipboardMenuComponent} from "@platform/components/shared/copy-value-to-clipboard-menu/copy-value-to-clipboard-menu.component"
import {JobResourceGroupPinLabels, JobResourceGroupPinOptions} from "@platform/models/state-labels/job-queue-group-labels"
import {eventView} from "@platform/components/jobs/job-task-details/helpers"
import {DatePipe} from "@angular/common"
import {from, of, switchMap, tap} from "rxjs"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-job-task-details",
    templateUrl: "job-task-details.component.html",
    styleUrls: ["job-task-details.component.scss"],
    imports: [
        DetailsDialogLayoutComponent,
        SectionComponent,
        MatInputModule,
        MatSelectModule,
        IsLoadingDirective,
        FormsModule,
        MatCheckboxModule,
        MatButtonModule,
        RoutedDialogComponent,
        MatMenu,
        MatMenuItem,
        MatMenuModule,
        FilesSectionComponent,
        TitleSectionComponent,
        CopyValueToClipboardMenuComponent,
        OrganizationSelectComponent,
        DatePipe,
    ],
})
export class JobTaskDetailsComponent extends BaseDetailsComponent<JobTaskDetailsFragment, Omit<MutationUpdateJobTaskInput, "id">> implements AfterViewInit {
    override _contentTypeModel = ContentTypeModel.JobTask
    override _fetchItem = fetchThrowingErrors(inject(JobTaskDetailsGQL))
    override _updateItem = mutateThrowingErrors(inject(UpdateJobTaskDetailsGQL))

    override paramName = "jobTaskId"
    override displayName = "Job Task"

    readonly injector = inject(Injector)

    // Exposed to the template for the task-event history list.
    protected readonly eventView = eventView

    private readonly updateResourceGroup = mutateThrowingErrors(inject(UpdateJobTaskResourceGroupDetailsGQL))

    // Labelled options rather than raw enum values, so the dropdown names the machine a task would be pinned
    // to ("Power Machine (spot)") instead of its internal id. Only the groups worth overriding onto are
    // offered (see JobResourceGroupPinOptions); the full label map still covers any historical pin.
    readonly $resourceGroupOptions = signal(JobResourceGroupPinOptions.map((id) => JobResourceGroupPinLabels.get(id)).filter(IsDefined))

    // The resource group the task is currently on: the active/completed cascade step, else the stored cascade
    // position, else the cascade's primary. Labelled for display, falling back to the raw id. Distinct from
    // the pin below (a manual override) — this is where the cascade has actually placed it.
    readonly $currentResourceGroup = computed(() => {
        const item = this.$item()
        if (!item) return null
        const cascade = item.cascade ?? []
        const active = cascade.find((step) => step.status === CascadeStepStatus.Active || step.status === CascadeStepStatus.Completed)
        const id = active?.resourceGroupId ?? item.cascadeGroupId ?? cascade[0]?.resourceGroupId ?? null
        return id ? (JobResourceGroupPinLabels.get(id)?.label ?? id) : null
    })

    async setResourceGroupPin(value: ResourceGroupId | null) {
        const id = this.$item()?.id
        if (!id) return
        await this.updateResourceGroup({input: {id, resourceGroupPin: value}})
    }

    readonly $copyItems = computed(() => {
        const result: {value: string | undefined | (() => string | undefined); displayText: string; icon: string}[] = [
            {value: this.$item()?.legacyId?.toString(), displayText: "Legacy ID", icon: "far fa-person-cane"},
            {value: this.$item()?.id, displayText: "ID", icon: "far fa-id-badge"},
        ]
        return result
    })

    async closeDialog() {
        const jobLegacyId = this.$item()?.job?.legacyId
        if (jobLegacyId) {
            await this.router.navigate(["jobs", jobLegacyId], {queryParamsHandling: "preserve"})
        }
    }

    formattedClaimedByField: string | undefined | null = undefined
    ngAfterViewInit() {
        this.item$
            .pipe(
                switchMap((item) =>
                    IsDefined(item) && isClaimedRenderJobTask(item) ? from(formatRenderJobTaskClaimedByField(item, this.injector)) : of(item),
                ),
                tap((maybeItem) => {
                    this.formattedClaimedByField = maybeItem?.claimedBy ?? null
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe()
    }

    protected readonly DialogSize = DialogSize
    protected readonly ContentTypeModel = ContentTypeModel
}
